'use client';

import { t } from '@lingui/macro';
import { CircularProgress, TextField } from '@mui/material';
import { Autocomplete } from '@prismo-io/design-system';
import useRunOnce from '@prismo-io/utils/client/use-run-once';
import { useCallback, useEffect, useState } from 'react';
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from 'react-hook-form';
import { useExperienceTypesAutocomplete } from './use-experience-name-autocomplete';

export type RomNameAutocompleteInputProps<F extends FieldValues = FieldValues> =
  {
    name: Path<F>;
    control: Control<F>;
    required?: boolean;
    label?: string;
    helperText?: string;
    disabled?: boolean;
  };

export function ExperienceNameAutocompleteInput<
  TFieldValues extends FieldValues,
>(props: RomNameAutocompleteInputProps<TFieldValues>) {
  const {
    name,
    control,
    required = false,
    label = undefined,
    helperText = undefined,
    disabled = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [inputTerm, setInputTerm] = useState<string | null>(null);

  const { data: options = [], isLoading } = useExperienceTypesAutocomplete({
    // isOpen,
    term: inputTerm,
  });

  const {
    field: { onChange, onBlur, value },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const onClose = useCallback(() => {
    setIsOpen(false);

    if (!isLoading) {
      if (value && typeof value === 'string' && value.trim() !== '') {
        const perfectMatch = options.find(
          (opt) =>
            value
              .trim()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase() ===
            opt.name
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
        );

        if (perfectMatch) {
          onChange(perfectMatch);
        }
      }
    }
  }, [setIsOpen, isLoading, value, options]);

  const [isInit, setIsInit] = useState<boolean>(false);

  useRunOnce({
    fn: () => {
      setIsInit(true);
    },
  });

  useEffect(() => {
    console.log('Value', value);
  }, [value]);

  return (
    <Autocomplete
      multiple={false}
      freeSolo
      handleHomeEndKeys
      open={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      value={value}
      loading={isLoading}
      loadingText={t`Recherche des noms experiences`}
      disabled={disabled}
      noOptionsText={t`Aucun résultat`}
      options={options}
      filterOptions={(options) => options}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
      onInputChange={(e, term) => {
        setInputTerm(term);

        if (isInit) {
          onChange(term);
        }
      }}
      onChange={(event, value, reason, details) => {
        onChange(value);
      }}
      onBlur={(event) => {
        onBlur();
      }}
      isOptionEqualToValue={(option, value) => {
        if (!value) {
          return false;
        }

        if (typeof value === 'string') {
          return value === option.name.normalize().toLowerCase();
        }

        return value ? option.code === (value?.code ?? value) : false;
      }}
      renderOption={(props, option) => {
        if (typeof option === 'string') {
          return (
            <li {...props} key={option}>
              {option}
            </li>
          );
        }

        return (
          <li {...props} key={option.code}>
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          label={label}
          {...params}
          error={!!error}
          InputLabelProps={{
            ...params.InputLabelProps,
            required,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
          }}
          helperText={error ? error.message : helperText}
        />
      )}
    />
  );
}
